import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

const STORAGE_KEY = 'veiga-law-dashboard-theme'
const THEME_LIGHT = 'light'
const THEME_DARK = 'dark'

const ThemeContext = createContext(null)

const isThemeSupported = (value) => value === THEME_LIGHT || value === THEME_DARK

const getSystemTheme = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return THEME_DARK
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches ? THEME_DARK : THEME_LIGHT
}

const getStoredTheme = () => {
  if (typeof window === 'undefined') {
    return null
  }

  const storedTheme = window.localStorage.getItem(STORAGE_KEY)
  return isThemeSupported(storedTheme) ? storedTheme : null
}

const resolveInitialTheme = () => getStoredTheme() || getSystemTheme()

const applyTheme = (theme) => {
  if (typeof document === 'undefined') {
    return
  }

  const root = document.documentElement
  root.setAttribute('data-theme', theme)
  root.style.colorScheme = theme
}

export function ThemeProvider({ children }) {
  const [theme, setActiveTheme] = useState(resolveInitialTheme)

  useEffect(() => {
    applyTheme(theme)
  }, [theme])

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      return undefined
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

    const handleSystemThemeChange = (event) => {
      if (getStoredTheme()) {
        return
      }

      setActiveTheme(event.matches ? THEME_DARK : THEME_LIGHT)
    }

    mediaQuery.addEventListener('change', handleSystemThemeChange)
    return () => mediaQuery.removeEventListener('change', handleSystemThemeChange)
  }, [])

  const setTheme = useCallback((nextTheme) => {
    if (!isThemeSupported(nextTheme)) {
      return
    }

    window.localStorage.setItem(STORAGE_KEY, nextTheme)
    setActiveTheme(nextTheme)
  }, [])

  const toggleTheme = useCallback(() => {
    setTheme(theme === THEME_DARK ? THEME_LIGHT : THEME_DARK)
  }, [setTheme, theme])

  const value = useMemo(
    () => ({
      theme,
      setTheme,
      toggleTheme,
      isDarkMode: theme === THEME_DARK,
      themes: {
        light: THEME_LIGHT,
        dark: THEME_DARK,
      },
    }),
    [setTheme, theme, toggleTheme],
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme() {
  const context = useContext(ThemeContext)

  if (!context) {
    throw new Error('useTheme deve ser usado dentro de ThemeProvider')
  }

  return context
}

export { STORAGE_KEY as THEME_STORAGE_KEY, THEME_LIGHT, THEME_DARK, resolveInitialTheme, applyTheme }
